/**
 * Helpers for the "Date de l'évènement" picker shared by the single-claim modal
 * (`SendEventModal`) and the batch island (`ClaimsBatchIsland`), both via
 * `EventDateTimePicker`.
 *
 * Three traps these exist to avoid — the first two produced timeline events dated *before*
 * the event they follow, which is what TECH-892 fixed:
 *
 *  1. Pre-filling the state with `new Date()`. Both hosts stay mounted for the whole page
 *     session, so the initializer runs at page load and the value is already stale by the
 *     time the admin submits. Leave the state `undefined` instead and let the API stamp
 *     `now()` at insert time — that is what the Vue dashboard did.
 *  2. Sending the raw value from `react-day-picker`. In `mode="single"` it returns local
 *     midnight, so picking today in Paris (UTC+2) yields *yesterday* 22:00 UTC — which is
 *     written as-is to `claims_events.event_date` (a `Timestamp(6)`, no timezone).
 *  3. Composing a datetime in the future. The calendar disables future *days*, but once
 *     minutes are selectable an admin can pick today and type 23:00 at 10:00 in the morning.
 *     An event that has not happened yet is nonsense, so `capToNow` pulls it back.
 */

/**
 * Carries the current wall-clock time onto a day picked in the calendar.
 *
 * Picking today therefore means "now", and picking an earlier day means "that day, at this
 * time of day" — never midnight, which would backdate the event behind everything that
 * already happened that day.
 */
export function withCurrentTimeOfDay(day: Date | undefined): Date | undefined {
  if (!day) {
    return undefined;
  }

  const now = new Date();
  const stamped = new Date(day);
  stamped.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

  return stamped;
}

/**
 * Moves a datetime onto a different day, keeping its time of day.
 *
 * Used when the admin picks a day in the calendar after already setting a time: the time they
 * chose deliberately wins over the current clock.
 */
export function withDayOf(date: Date, day: Date): Date {
  const moved = new Date(day);
  moved.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());

  return moved;
}

/**
 * Applies an `<input type="time">` value (`"HH:mm"`, or `""` when the field is cleared) to a
 * day. Seconds are zeroed: a time typed to the minute should mean that minute exactly, not
 * that minute plus whatever seconds the clock happened to carry.
 *
 * Returns `undefined` for an empty or malformed value so the caller can fall back to
 * "maintenant".
 */
export function withTimeInputValue(day: Date, timeValue: string): Date | undefined {
  const match = /^(\d{1,2}):(\d{2})$/.exec(timeValue);
  if (!match) {
    return undefined;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return undefined;
  }

  const stamped = new Date(day);
  stamped.setHours(hours, minutes, 0, 0);

  return stamped;
}

/** Renders a date as an `<input type="time">` value, or `""` when there is no date yet. */
export function toTimeInputValue(date: Date | undefined): string {
  if (!date) {
    return "";
  }

  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/**
 * Pulls a future datetime back to now. An event is something that already happened, so a
 * future `eventDate` is always a mistake — and capping needs no error state.
 */
export function capToNow(date: Date | undefined): Date | undefined {
  if (!date) {
    return undefined;
  }

  const now = new Date();

  return date.getTime() > now.getTime() ? now : date;
}
